import React, { createContext, useContext, useEffect, useState } from "react";
import { Modal, Drawer } from "@heroui/react";

// One shared dialog primitive that renders as a centered Modal on desktop
// (the default) or a right-side Drawer on desktop when variant="drawer" is
// requested (e.g. a details panel that should always be a drawer), but on
// a mobile-width viewport ALWAYS renders as a bottom-sheet Drawer
// regardless of variant, since a centered modal or a right-edge drawer are
// both cramped on a narrow screen. Callers write one JSX tree
// (DialogDrawer.Header/Body/Footer/...) and never branch on viewport
// themselves; which underlying HeroUI primitive is active is tracked in
// context and each sub-component picks the matching real component.
const MOBILE_BREAKPOINT_PX = 640; // matches Tailwind's `sm` breakpoint

function useIsMobile(breakpointPx = MOBILE_BREAKPOINT_PX) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpointPx
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const handler = () => setIsMobile(mql.matches);
    handler();
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [breakpointPx]);

  return isMobile;
}

type DialogDrawerMode = "modal" | "drawer";

interface DialogDrawerState {
  mode: DialogDrawerMode;
  placement: "right" | "bottom" | null;
}

const DialogDrawerContext = createContext<DialogDrawerState>({ mode: "modal", placement: null });

function useDialogDrawerState() {
  return useContext(DialogDrawerContext);
}

// Replicates HeroUI's .modal__icon classes (see Modal's styles.css) so a
// header icon looks identical whether it renders through the real
// Modal.Icon (modal mode) or this plain div (drawer mode, where Drawer has
// no Icon slot of its own).
const ICON_CLASSES = "flex items-center justify-center size-10 shrink-0 rounded-3xl select-none";

interface DialogDrawerBackdropProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isDismissable?: boolean;
  variant?: DialogDrawerMode;
  /** Forwarded to Modal.Container only (e.g. "inside" for a scrolling body with a fixed header/footer); Drawer's body scrolls internally by default and ignores this. */
  scroll?: "inside" | "outside";
  children: React.ReactNode;
}

function DialogDrawerBackdrop({
  isOpen,
  onOpenChange,
  isDismissable,
  variant = "modal",
  scroll,
  children,
}: DialogDrawerBackdropProps) {
  const isMobile = useIsMobile();
  const mode: DialogDrawerMode = isMobile ? "drawer" : variant;
  const placement: DialogDrawerState["placement"] = mode === "drawer" ? (isMobile ? "bottom" : "right") : null;

  if (mode === "drawer") {
    return (
      <DialogDrawerContext.Provider value={{ mode, placement }}>
        <Drawer.Backdrop isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={isDismissable}>
          <Drawer.Content placement={placement!}>{children}</Drawer.Content>
        </Drawer.Backdrop>
      </DialogDrawerContext.Provider>
    );
  }

  return (
    <DialogDrawerContext.Provider value={{ mode, placement }}>
      <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={isDismissable}>
        <Modal.Container scroll={scroll}>{children}</Modal.Container>
      </Modal.Backdrop>
    </DialogDrawerContext.Provider>
  );
}

function DialogDrawerDialog({ className, children }: { className?: string; children: React.ReactNode }) {
  const { mode, placement } = useDialogDrawerState();
  if (mode === "drawer") {
    return (
      <Drawer.Dialog className={className}>
        {placement === "bottom" && <Drawer.Handle />}
        {children}
      </Drawer.Dialog>
    );
  }
  return <Modal.Dialog className={className}>{children}</Modal.Dialog>;
}

function DialogDrawerCloseTrigger() {
  const { mode } = useDialogDrawerState();
  return mode === "drawer" ? <Drawer.CloseTrigger /> : <Modal.CloseTrigger />;
}

function DialogDrawerHeader({ className, children }: { className?: string; children: React.ReactNode }) {
  const { mode } = useDialogDrawerState();
  return mode === "drawer" ? (
    <Drawer.Header className={className}>{children}</Drawer.Header>
  ) : (
    <Modal.Header className={className}>{children}</Modal.Header>
  );
}

function DialogDrawerIcon({ className, children }: { className?: string; children: React.ReactNode }) {
  const { mode } = useDialogDrawerState();
  if (mode === "modal") return <Modal.Icon className={className}>{children}</Modal.Icon>;
  return (
    <div className={[ICON_CLASSES, className].filter(Boolean).join(" ")} data-slot="drawer-icon">
      {children}
    </div>
  );
}

function DialogDrawerHeading({ children }: { children: React.ReactNode }) {
  const { mode } = useDialogDrawerState();
  return mode === "drawer" ? <Drawer.Heading>{children}</Drawer.Heading> : <Modal.Heading>{children}</Modal.Heading>;
}

function DialogDrawerBody({ className, children }: { className?: string; children: React.ReactNode }) {
  const { mode } = useDialogDrawerState();
  return mode === "drawer" ? (
    <Drawer.Body className={className}>{children}</Drawer.Body>
  ) : (
    <Modal.Body className={className}>{children}</Modal.Body>
  );
}

function DialogDrawerFooter({ className, children }: { className?: string; children: React.ReactNode }) {
  const { mode } = useDialogDrawerState();
  return mode === "drawer" ? (
    <Drawer.Footer className={className}>{children}</Drawer.Footer>
  ) : (
    <Modal.Footer className={className}>{children}</Modal.Footer>
  );
}

export const DialogDrawer = {
  Backdrop: DialogDrawerBackdrop,
  Dialog: DialogDrawerDialog,
  CloseTrigger: DialogDrawerCloseTrigger,
  Header: DialogDrawerHeader,
  Icon: DialogDrawerIcon,
  Heading: DialogDrawerHeading,
  Body: DialogDrawerBody,
  Footer: DialogDrawerFooter,
};
